import { theme } from "@src/theme";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import styled, { keyframes as Keyframes, css } from "styled-components";

export type INotification = {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
  $isExiting?: boolean;
};

type NotificationContextType = {
  addNotification: (
    message: string,
    type: INotification["type"],
    durationMs?: number
  ) => void;
};

/** How long a notification stays up before it starts fading out. */
const DEFAULT_DURATION_MS = 3000;
/** Must match the fadeOut animation below, so the node is removed once hidden. */
const EXIT_ANIMATION_MS = 500;

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

const fadeIn = Keyframes`
    from {
        opacity: 0;
        transform: translateX(100%);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const fadeOut = Keyframes`
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(100%);
    }
`;

const NotificationWrapper = styled.div`
  position: fixed;
  top: 40px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
`;

const NotificationItem = styled.div<{
  $isExiting: boolean;
  type: INotification["type"];
}>`
  background-color: ${theme.colors.darkBg};
  color: ${({ type }): string =>
    type === "success" ? theme.colors.white : theme.colors.white};
  font-weight: 700;
  padding: 20px 40px;
  max-width: 200px;
  border-radius: 5px;
  border: 2px solid
    ${({ type }): string =>
      type === "success"
        ? theme.colors.green
        : type === "error"
          ? theme.colors.red
          : type === "info"
            ? theme.colors.white
            : theme.colors.yellow};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
  animation: ${(props) => css`
    ${props.$isExiting ? fadeOut : fadeIn} 0.5s ease-out
  `};
`;

const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<INotification[]>([]);
  // Stable identity (only uses the setState setter) so consumers can safely
  // depend on addNotification in effect dependency arrays without re-running
  // on every provider render.
  const addNotification = useCallback(
    (
      message: string,
      type: INotification["type"],
      durationMs: number = DEFAULT_DURATION_MS
    ): void => {
      const id = crypto.randomUUID();
      setNotifications((prev) => [...prev, { id, message, type }]);
      setTimeout(() => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, $isExiting: true } : n))
        );
      }, durationMs);
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, durationMs + EXIT_ANIMATION_MS);
    },
    []
  );

  const contextValue = useMemo(() => ({ addNotification }), [addNotification]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationWrapper>
        {notifications.map((notification) => (
          <NotificationItem
            $isExiting={notification?.$isExiting || false}
            key={notification.id}
            type={notification.type}
          >
            {notification.message}
          </NotificationItem>
        ))}
      </NotificationWrapper>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
